import { Alert, Card, Center, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { QueryKey } from "@tanstack/react-query";
import { Table as ReactTable } from "@tanstack/react-table";
import { forwardRef, useImperativeHandle } from "react";
import NoData from "@/components/no-data";
import Table from "@/components/table";
import ResetTableButton from "@/components/table/ResetTableButton";
import { THandler } from "@/components/table/Table";
import TablePagination from "@/components/table/TablePagination";
import useQueryTable from "@/components/table/useQueryTable";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import projectColumns from "./Columns";
import { DEFAULT_PAGINATION, DEFAULT_SORTING, getQuestionarios } from "../../firestore";

export const QUERY_KEY = "questionarios";

const QuestionariosTable = forwardRef<THandler<TQuestionario>>((_properties, reference) => {
  const uid = getCurrentUser()?.uid;
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const projeto = useUserStore((state) => state.activeProjeto);

  const { table, data, isLoading, error, filters, refetch } = useQueryTable<TQuestionario>({
    queryKey: [QUERY_KEY, projeto],
    enabled: !!uid && !!activeEmpresaId,
    subjectType: "TQuestionario",
    columns: projectColumns,
    initialState: {
      columnFilters: [],
      globalFilter: "",
      pagination: DEFAULT_PAGINATION,
      sorting: DEFAULT_SORTING
    },
    queryFunction: ({ sorting, filters, pagination, paginationCursors }) => {
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }

      const newFilter = projeto ? filters?.concat([{ id: "idProjeto", value: ["==", projeto] }]) : filters;

      return getQuestionarios(activeEmpresaId, {
        sorting,
        filters: newFilter,
        pagination,
        paginationCursors
      });
    }
  });

  useImperativeHandle<THandler<TQuestionario>, THandler<TQuestionario>>(reference, () => {
    return {
      table,
      data,
      isLoading,
      error,
      filters
    };
  }, [filters, data, error, isLoading, table]);

  /**
   * Handles the close of the error alert.
   */
  function handleErrorClose() {
    void refetch();
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title="Erro ao buscar questionarios"
        color="red"
        withCloseButton
        onClose={handleErrorClose}
      >
        {error.message}
      </Alert>
    );
  }

  const isEmpty = !isLoading && data?.entries.length === 0;

  return (
    <>
      <Card.Section withBorder>
        <Table table={table} isLoading={isLoading} />
        {isEmpty && !isLoading && (
          <Center m="lg">
            <NoData m="md">
              <Stack gap="xs">
                <Text c="dimmed">Nenhum Questionario Encontrado!</Text>
                <ResetTableButton table={table as ReactTable<unknown>} queryKey={QUERY_KEY as unknown as QueryKey}>
                  Limpar filtros
                </ResetTableButton>
              </Stack>
            </NoData>
          </Center>
        )}
      </Card.Section>
      {reference && "current" in reference && reference.current !== null ? (
        <Card.Section withBorder>
          <TablePagination tableHandlers={reference.current} />
        </Card.Section>
      ) : null}
    </>
  );
});

QuestionariosTable.displayName = "QuestionariosTable";

export default QuestionariosTable;
